'use client'

import { env } from '@/lib/runtime-env'

import { IFRAME_SEARCH_PARAM_KEYS, IframeSearchParamKeys } from '@/constants/iframe'
import { appendQueryString } from '@/lib/url'
import { IframePolicy, IframeSettings } from '@/types/scene'

const createEnvSettings = (envKey: string) =>
  envKey
    ?.split('|')
    .map((item) => item.trim())
    .filter((item) => item) || []

export const getStandaloneIframeSandboxPermissions = (
  iframeSettings: IframeSettings | IframePolicy[],
) => {
  const envSettings = createEnvSettings(env('NEXT_PUBLIC_DEFAULT_IFRAME_SANDBOX_PERMISSIONS')!)

  const baseSettings =
    process.env.NODE_ENV === 'development'
      ? ['allow-same-origin', 'allow-scripts', 'allow-popups', 'allow-forms', 'allow-pointer-lock']
      : ['allow-scripts']

  const settings = Array.isArray(iframeSettings) ? iframeSettings : iframeSettings?.sandbox || []
  const sandboxSettings = Array.from(new Set([...settings, ...envSettings, ...baseSettings]))
  return sandboxSettings
}

export const getStandaloneIframeAllowPermissions = (
  iframeSettings: IframeSettings | IframePolicy[],
) => {
  const envSettings = createEnvSettings(env('NEXT_PUBLIC_DEFAULT_IFRAME_ALLOW_PERMISSIONS')!)

  const baseSettings =
    process.env.NODE_ENV === 'development'
      ? ['autoplay', 'encrypted-media', 'fullscreen', 'picture-in-picture', 'web-share']
      : []

  const settings = Array.isArray(iframeSettings) ? [] : iframeSettings?.allow || []
  const allowSettings = Array.from(new Set([...settings, ...envSettings, ...baseSettings]))
  return allowSettings
}

export const getWorldIframeAllowPermissions = () => {
  const envSettings = createEnvSettings(env('NEXT_PUBLIC_DEFAULT_WORLD_IFRAME_ALLOW_PERMISSIONS')!)
  return envSettings
}

type IframeUrlParams = Partial<Record<IframeSearchParamKeys, boolean>> & {
  otherParams?: Record<string, unknown>
}

export function buildIframeUrl(baseUrl: string, params: IframeUrlParams = {}): string {
  const searchParams = new URLSearchParams()

  if (params.full3d) {
    searchParams.append(IFRAME_SEARCH_PARAM_KEYS.FULL3D, '')
  }

  if (params['no-cookie']) {
    searchParams.append(IFRAME_SEARCH_PARAM_KEYS.NO_COOKIE, '')
  }

  if (params.view) {
    searchParams.append(IFRAME_SEARCH_PARAM_KEYS.VIEW, '')
  }

  if (params['no-scrolllock']) {
    searchParams.append(IFRAME_SEARCH_PARAM_KEYS.NO_SCROLLLOCK, '')
  }

  // 🥷🏼 Add all other params here
  Object.entries(params.otherParams || {}).forEach(([key, value]) => {
    searchParams.append(key, value as string)
  })

  return appendQueryString(baseUrl, searchParams.toString())
}

export function isValidBase64(str: string): boolean {
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
  if (!base64Regex.test(str) || str.length % 4 !== 0) return false
  try {
    return btoa(atob(str)) === str
  } catch {
    return false
  }
}

export function buildStandaloneIframeUrl(
  standaloneUrl: string,
  appState: string | null,
  isVideoDebug: boolean,
): string {
  let url: URL

  // Validate the standaloneUrl to ensure it's a valid URL
  try {
    url = new URL(standaloneUrl)
  } catch {
    return ''
  }

  if (appState && isValidBase64(appState)) {
    if (isVideoDebug) {
      try {
        const parsed = JSON.parse(atob(appState)) as Record<string, unknown>
        parsed.debug = true
        url.searchParams.set('app_state', btoa(JSON.stringify(parsed)))
      } catch {
        url.searchParams.set('app_state', appState)
      }
    } else {
      url.searchParams.set('app_state', appState)
    }
    return url.toString()
  }

  if (isVideoDebug) {
    url.searchParams.set('app_state', btoa(JSON.stringify({ debug: true })))
    return url.toString()
  }

  return standaloneUrl
}
